import {useCallback, useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {NivelApilado, NivelPromedio} from '@/components/bateria/nivel-charts'
import {NivelTag} from '@/components/bateria/nivel-tag'
import {VistaPills} from '@/components/bateria/vista-pills'
import type {NivelDistribucion} from '@/hooks/bateria/use-bateria'
import {prettifyTaxonomia, totalPersonas} from '@/lib/bateria'
import {entradasConDatos, ordenarPeorPrimero} from '@/lib/bateria-charts'

type Modo = 'reparto' | 'promedio'

/** La tabla lista todas las entidades con el nombre completo; las gráficas omiten las vacías. */
export function TaxonomiaBloque({
  data,
  ariaLabel,
  idPrefix,
  testId,
}: {
  data: Record<string, NivelDistribucion>
  ariaLabel: string
  idPrefix: string
  testId: string
}) {
  const {t} = useTranslation()
  const [modo, setModo] = useState<Modo>('reparto')

  const conDatos = useMemo(() => entradasConDatos(data), [data])
  const todas = useMemo(
    () =>
      ordenarPeorPrimero(Object.entries(data).map(([clave, dist]) => ({clave, label: prettifyTaxonomia(clave), dist}))),
    [data]
  )

  // Estable: ApexCharts reaplica las opciones enteras con cada identidad nueva y corta la animación.
  const irADetalle = useCallback(
    (clave: string) => {
      const fila = document.getElementById(`${idPrefix}-detalle-${clave}`)
      if (!fila) return
      fila.scrollIntoView({behavior: 'smooth', block: 'center'})
      fila.focus({preventScroll: true})
    },
    [idPrefix]
  )

  return (
    <div className="space-y-3" data-testid={testId}>
      <VistaPills
        label={t('admin.bateria.resultados.modoLabel')}
        testIdPrefix={`${idPrefix}-modo`}
        valor={modo}
        onPick={setModo}
        opciones={[
          {key: 'reparto', label: t('admin.bateria.resultados.modoReparto')},
          {key: 'promedio', label: t('admin.bateria.resultados.modoPromedio')},
        ]}
      />

      {conDatos.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('admin.bateria.resultados.sinValidos')}</p>
      ) : modo === 'reparto' ? (
        <>
          <NivelApilado
            entradas={conDatos}
            ariaLabel={ariaLabel}
            testId={`${idPrefix}-apilado-chart`}
            onPick={irADetalle}
          />
        </>
      ) : (
        <NivelPromedio entradas={conDatos} ariaLabel={ariaLabel} testId={`${idPrefix}-promedio-chart`} />
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-border font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
              <th className="py-2 pr-3">{t('admin.bateria.resultados.comparacionEntidad')}</th>
              <th className="py-2 pr-3">{t('admin.bateria.resultados.comparacionNivel')}</th>
              <th className="py-2 pr-3 text-right">{t('admin.bateria.resultados.comparacionPromedio')}</th>
              <th className="py-2 text-right">{t('admin.bateria.resultados.colPersonas')}</th>
            </tr>
          </thead>
          <tbody>
            {todas.map(({clave, label, dist}) => (
              <tr
                key={clave}
                id={`${idPrefix}-detalle-${clave}`}
                tabIndex={-1}
                data-testid={`${idPrefix}-detalle-${clave}`}
                className="surface-divider border-b outline-none last:border-0 focus-visible:bg-accent/40">
                <td className="py-2 pr-3 text-foreground">{label}</td>
                <td className="py-2 pr-3">
                  <NivelTag dist={dist} />
                </td>
                <td className="py-2 pr-3 text-right font-mono text-xs tabular-nums">
                  {dist.promedio_transformado === null ? '—' : dist.promedio_transformado.toFixed(1)}
                </td>
                <td className="py-2 text-right font-mono text-xs tabular-nums">{totalPersonas(dist)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
